import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import "./store-status-admin-toggle.css";

interface StoreStatus {
  isOpen: boolean;
  withinHours: boolean;
  message: string | null;
  expectedReturnAt: string | null;
}

/**
 * Admin-only toggle for the "Volta Já" banner. One click to mark the store
 * temporarily closed (with optional message + return time); one click to
 * reopen. Lives in the admin layout header.
 */
export function StoreStatusAdminToggle() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<StoreStatus>({
    queryKey: ["store-status"],
    queryFn: () => api<StoreStatus>("/api/store-status"),
    retry: false,
  });
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [returnTime, setReturnTime] = useState("");

  const mutate = useMutation({
    mutationFn: (input: {
      isOpen: boolean;
      message?: string | null;
      expectedReturnAt?: string | null;
    }) => api<{ ok: true }>("/api/admin/store-status", { method: "PUT", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["store-status"] });
      setEditing(false);
      setMessage("");
      setReturnTime("");
    },
  });

  if (isLoading || !data) return null;

  if (data.isOpen && !editing) {
    // Not "away", but the weekly schedule still decides whether it's actually
    // open right now (closed day / outside hours → show a muted closed state).
    const openNow = data.withinHours;
    return (
      <div
        className={`store-toggle ${openNow ? "store-toggle--open" : "store-toggle--scheduled-closed"}`}
      >
        <span
          className={`store-toggle__dot${openNow ? "" : " store-toggle__dot--muted"}`}
          aria-hidden="true"
        />
        <span>{openNow ? t("admin.storeStatus.open") : t("admin.storeStatus.closedSchedule")}</span>
        <button
          type="button"
          className="store-toggle__action"
          onClick={() => setEditing(true)}
        >
          {t("admin.storeStatus.markAway")}
        </button>
      </div>
    );
  }

  if (!data.isOpen) {
    return (
      <div className="store-toggle store-toggle--closed">
        <span className="store-toggle__dot store-toggle__dot--alert" aria-hidden="true" />
        <span>{t("admin.storeStatus.away")} {data.message ? `— ${data.message}` : ""}</span>
        <button
          type="button"
          className="store-toggle__action"
          onClick={() => mutate.mutate({ isOpen: true })}
          disabled={mutate.isPending}
        >
          {mutate.isPending ? t("admin.storeStatus.opening") : t("admin.storeStatus.reopen")}
        </button>
      </div>
    );
  }

  // editing form
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const expectedReturnAt = returnTime
      ? new Date(returnTime).toISOString()
      : null;
    mutate.mutate({
      isOpen: false,
      message: message.trim() || null,
      expectedReturnAt,
    });
  }

  return (
    <form className="store-toggle store-toggle--editing" onSubmit={onSubmit}>
      <input
        type="text"
        placeholder={t("admin.storeStatus.messagePlaceholder")}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={200}
        aria-label={t("admin.storeStatus.messageAria")}
      />
      <input
        type="datetime-local"
        value={returnTime}
        onChange={(e) => setReturnTime(e.target.value)}
        aria-label={t("admin.storeStatus.returnAria")}
      />
      <button type="submit" className="store-toggle__action" disabled={mutate.isPending}>
        {mutate.isPending ? t("admin.storeStatus.closing") : t("admin.storeStatus.close")}
      </button>
      <button
        type="button"
        className="store-toggle__cancel"
        onClick={() => setEditing(false)}
      >
        {t("common.cancel")}
      </button>
    </form>
  );
}
